import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GiftGuide = ({ product }) => {
  const [activeTab, setActiveTab] = useState('occasions');

  const giftingOccasions = [
    {
      icon: 'Calendar',
      title: 'Birthday',
      description: 'Perfect for celebrating another year of life',
      popularity: 95,
      tips: ['Add a personal note', 'Consider their hobbies', 'Wrap it beautifully']
    },
    {
      icon: 'Heart',
      title: 'Anniversary',
      description: 'Show your love and appreciation',
      popularity: 88,
      tips: ['Include a romantic card', 'Choose timeless appeal', 'Make it memorable']
    },
    {
      icon: 'Star',
      title: 'Achievement',
      description: 'Celebrate their success and hard work',
      popularity: 76,
      tips: ['Acknowledge their effort', 'Choose something motivational', 'Celebrate the moment']
    },
    {
      icon: 'Gift',
      title: 'Just Because',
      description: 'Surprise them with spontaneous kindness',
      popularity: 82,
      tips: ['Keep it simple', 'Focus on thoughtfulness', 'Surprise timing is key']
    }
  ];

  const recipientPersonas = [
    {
      icon: 'Palette',
      title: 'The Creative',
      description: 'Artistic souls who love unique and inspiring gifts',
      traits: ['Artistic', 'Imaginative', 'Expressive'],
      whyItWorks: 'Appeals to their aesthetic sense and creative spirit'
    },
    {
      icon: 'BookOpen',
      title: 'The Intellectual',
      description: 'Thoughtful individuals who appreciate depth and meaning',
      traits: ['Curious', 'Analytical', 'Thoughtful'],
      whyItWorks: 'Stimulates their mind and satisfies their quest for knowledge'
    },
    {
      icon: 'Coffee',
      title: 'The Comfort Lover',
      description: 'People who enjoy cozy moments and simple pleasures',
      traits: ['Relaxed', 'Appreciative', 'Peaceful'],
      whyItWorks: 'Enhances their daily comfort and brings joy to routine'
    },
    {
      icon: 'Compass',
      title: 'The Adventurer',
      description: 'Active people who love experiences and exploration',
      traits: ['Energetic', 'Curious', 'Bold'],
      whyItWorks: 'Supports their active lifestyle and adventurous spirit'
    }
  ];

  const giftingTips = [
    {
      icon: 'Clock',
      title: 'Timing Matters',
      description: 'Order 3-5 days before the occasion for stress-free gifting',
      category: 'planning'
    },
    {
      icon: 'Package',
      title: 'Presentation is Key',
      description: 'Beautiful packaging makes even simple gifts feel special',
      category: 'presentation'
    },
    {
      icon: 'MessageSquare',
      title: 'Add a Personal Touch',
      description: 'Include a handwritten note explaining why you chose this gift',
      category: 'personalization'
    },
    {
      icon: 'Users',
      title: 'Know Your Recipient',
      description: 'Consider their personality, interests, and current life situation',
      category: 'selection'
    }
  ];

  const tabs = [
    { id: 'occasions', label: 'Perfect Occasions', icon: 'Calendar' },
    { id: 'personas', label: 'Recipient Types', icon: 'Users' },
    { id: 'tips', label: 'Gifting Tips', icon: 'Lightbulb' }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'occasions':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {giftingOccasions.map((occasion, index) => (
              <div key={index} className="bg-white rounded-2xl border border-accent-100 p-5 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center">
                    <Icon name={occasion.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-heading font-semibold text-accent-800">
                        {occasion.title}
                      </h3>
                      <div className="flex items-center space-x-1">
                        <span className="text-sm font-medium text-success">
                          {occasion.popularity}%
                        </span>
                        <Icon name="TrendingUp" size={14} className="text-success" />
                      </div>
                    </div>
                    <p className="text-accent-600 text-sm mb-3">
                      {occasion.description}
                    </p>
                    <div className="space-y-1">
                      {occasion.tips.map((tip, tipIndex) => (
                        <div key={tipIndex} className="flex items-center space-x-2">
                          <Icon name="Check" size={12} className="text-success" />
                          <span className="text-xs text-accent-600">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'personas':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recipientPersonas.map((persona, index) => (
              <div key={index} className="bg-white rounded-2xl border border-accent-100 p-5 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-secondary-50 rounded-xl flex items-center justify-center">
                    <Icon name={persona.icon} size={24} className="text-secondary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-accent-800 mb-2">
                      {persona.title}
                    </h3>
                    <p className="text-accent-600 text-sm mb-3">
                      {persona.description}
                    </p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {persona.traits.map((trait, traitIndex) => (
                        <span
                          key={traitIndex}
                          className="px-2 py-1 bg-accent-100 text-accent-600 text-xs rounded-full"
                        >
                          {trait}
                        </span>
                      ))}
                    </div>
                    <div className="bg-secondary-50 rounded-xl p-3">
                      <div className="flex items-start space-x-2">
                        <Icon name="Lightbulb" size={14} className="text-secondary mt-0.5" />
                        <p className="text-xs text-secondary-800 leading-relaxed">
                          {persona.whyItWorks}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'tips':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {giftingTips.map((tip, index) => (
              <div key={index} className="bg-white rounded-2xl border border-accent-100 p-5 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-accent-50 rounded-xl flex items-center justify-center">
                    <Icon name={tip.icon} size={24} className="text-accent-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-accent-800 mb-2">
                      {tip.title}
                    </h3>
                    <p className="text-accent-600 text-sm mb-3">
                      {tip.description}
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-accent-100 rounded-full">
                      <span className="text-xs font-medium text-accent-700 capitalize">
                        {tip.category}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-heading text-2xl font-bold text-accent-800 mb-2">
          Gift Giving Guide
        </h2>
        <p className="text-accent-600 max-w-2xl mx-auto">
          Make every gift memorable with our expert recommendations and insights
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center">
        <div className="bg-accent-50 rounded-2xl p-1 flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white text-accent-800 shadow-soft'
                  : 'text-accent-600 hover:text-accent-800'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="bg-accent-50 rounded-2xl p-6">
        {renderContent()}
      </div>

      {/* Gift Concierge CTA */}
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-6 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-soft">
            <Icon name="MessageCircle" size={32} className="text-primary" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-accent-800 mb-2">
            Need Personal Gift Advice?
          </h3>
          <p className="text-accent-600 mb-4">
            Our gift experts are here to help you find the perfect present
          </p>
          <Button
            variant="primary"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={() => console.log('Open gift concierge')}
            className="shadow-warm"
          >
            Chat with Gift Expert
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GiftGuide;